// Cross-taxonomy crosswalk
//
// The platform has three independent taxonomy families that each answer a
// slightly different question but share overlapping concepts:
//
//   - Demand taxonomy      (demand-generation.ts): DEMAND_TACTICS, DEMAND_CHANNELS ...
//   - Initiative/activation (initiatives.ts):       INITIATIVE_TAG_OPTIONS (activities) ...
//   - Entity/directory      (index.ts):             CAPABILITY_OPTIONS, CHANNEL_EXPERTISE_OPTIONS ...
//
// These families intentionally keep their own id values (so no stored data has
// to change) but several concepts are genuine near-duplicates — e.g. a demand
// tactic "product-sampling" is the same idea as the initiative activity
// "product_sampling" and the entity capability "sampling-programs".
//
// This file is the single authoritative place that records those equivalences.
// It is additive and read-only: nothing here rewrites stored values. Use these
// maps for cross-linking (e.g. "show activations related to this demand tactic"
// or "find directory providers for this delivery activity") instead of
// hardcoding id string literals in components.

import type {
  DemandChannel,
  DemandDeliveryActivity,
  DemandInspirationAttributes,
  DemandTactic,
} from './demand-generation';

/** Initiative activity id (a subset of INITIATIVE_TAG_OPTIONS with scope 'activity'). */
export type InitiativeActivityId = string;

/** Entity directory capability id (from CAPABILITY_OPTIONS in constants/index.ts). */
export type EntityCapabilityId = string;

/** Entity directory channel-expertise id (from CHANNEL_EXPERTISE_OPTIONS). */
export type EntityChannelExpertiseId = string;

/**
 * Demand tactic -> equivalent initiative activity id.
 *
 * Only tactics that have a real activation-activity counterpart are listed;
 * a missing entry means there is no direct initiative equivalent.
 */
export const DEMAND_TACTIC_TO_INITIATIVE_ACTIVITY: Partial<
  Record<DemandTactic, InitiativeActivityId>
> = {
  'product-sampling': 'product_sampling',
  'product-demonstration': 'product_demo',
  'product-launch': 'product_launch',
  'competition-giveaway': 'promotional_giveaway',
  'experiential-activation': 'pop_up',
};

/** Reverse lookup: initiative activity id -> demand tactic id. */
export const INITIATIVE_ACTIVITY_TO_DEMAND_TACTIC: Partial<
  Record<InitiativeActivityId, DemandTactic>
> = Object.fromEntries(
  Object.entries(DEMAND_TACTIC_TO_INITIATIVE_ACTIVITY).map(([tactic, activity]) => [
    activity,
    tactic as DemandTactic,
  ])
);

/**
 * Demand delivery activity -> equivalent entity directory capability id.
 *
 * These connect the "what's needed to execute" work on the demand page to the
 * service providers advertising that capability in the directory. Only genuine
 * overlaps are mapped.
 */
export const DEMAND_DELIVERY_ACTIVITY_TO_ENTITY_CAPABILITY: Partial<
  Record<DemandDeliveryActivity, EntityCapabilityId>
> = {
  // Strategy & insight
  'shopper-insights-research': 'consumer-research',
  'campaign-planning': 'marketing-strategy',
  'offer-development': 'commercial-strategy',
  // Creative & design
  'packaging-design': 'packaging-design',
  'point-of-sale-design': 'creative-production',
  'display-design-build': 'creative-production',
  'campaign-creative-development': 'creative-production',
  'visitor-experience-design': 'creative-production',
  // Production
  'content-production': 'creative-production',
  'print-production': 'creative-production',
  'promotional-packaging-design': 'packaging-design',
  'signage-shelf-material-production': 'creative-production',
  'merchandise-production': 'creative-production',
  // Media & placement
  'media-planning-placement': 'digital-marketing',
  // Logistics & distribution
  'kitting-assembly': 'logistics-fulfilment',
  'warehousing-storage': 'logistics-fulfilment',
  'distribution-delivery': 'logistics-fulfilment',
  'campaign-deployment': 'logistics-fulfilment',
  'asset-management': 'logistics-fulfilment',
  // Activation & staffing
  'sampling-coordination': 'sampling-programs',
  'activation-staffing': 'activations-events',
  'shopper-education': 'activations-events',
  'event-setup': 'activations-events',
  // Measurement & optimisation
  'campaign-measurement-reporting': 'data-insights',
  'referral-tracking': 'data-insights',
};

/**
 * Demand tactic -> equivalent entity directory capability id.
 *
 * Powers the "Providers" link on each tactic card on the /demand page: given a
 * tactic a brand is interested in, resolve to the directory capability a
 * service provider would advertise to deliver it. Only tactics with a genuine
 * provider-side counterpart are mapped; a missing entry means the tactic has no
 * direct directory equivalent (so no Providers link is shown).
 */
export const DEMAND_TACTIC_TO_ENTITY_CAPABILITY: Partial<Record<DemandTactic, EntityCapabilityId>> =
  {
    'product-sampling': 'sampling-programs',
    'product-demonstration': 'activations-events',
    'retail-display-pos': 'creative-production',
    'retail-media': 'digital-marketing',
    'promotion-offer': 'marketing-strategy',
    'loyalty-programme': 'digital-marketing',
    'competition-giveaway': 'digital-marketing',
    'packaging-activation': 'packaging-design',
    collaboration: 'marketing-strategy',
    'limited-edition-scarcity': 'branding',
    'product-launch': 'marketing-strategy',
    'content-social-campaign': 'social-media',
    'experiential-activation': 'activations-events',
    'earned-media-advocacy': 'marketing-strategy',
  };

/** Reverse lookup: entity capability id -> demand delivery activity ids (may be many). */
export const ENTITY_CAPABILITY_TO_DEMAND_DELIVERY_ACTIVITIES: Partial<
  Record<EntityCapabilityId, DemandDeliveryActivity[]>
> = Object.entries(DEMAND_DELIVERY_ACTIVITY_TO_ENTITY_CAPABILITY).reduce(
  (acc, [activity, capability]) => {
    (acc[capability] ??= []).push(activity as DemandDeliveryActivity);
    return acc;
  },
  {} as Record<EntityCapabilityId, DemandDeliveryActivity[]>
);

/**
 * Demand channel -> entity directory channel-expertise id.
 *
 * The two channel lists overlap only partially (demand channels are coarse
 * touchpoints, channel-expertise ids are finer route-to-market granularity).
 * `ecommerce-dtc` is the one exact shared id; the rest map coarse -> the
 * closest finer-grained expertise id.
 */
export const DEMAND_CHANNEL_TO_ENTITY_CHANNEL_EXPERTISE: Partial<
  Record<DemandChannel, EntityChannelExpertiseId>
> = {
  'retail-in-store': 'grocery-multiples',
  'ecommerce-dtc': 'ecommerce-dtc',
  hospitality: 'foodservice',
  'consumer-events': 'events-festivals',
};

/** Resolve the initiative activity id for a demand tactic, if one exists. */
export function getInitiativeActivityForTactic(
  tactic: DemandTactic
): InitiativeActivityId | undefined {
  return DEMAND_TACTIC_TO_INITIATIVE_ACTIVITY[tactic];
}

/** Resolve the entity directory capability id for a demand delivery activity, if one exists. */
export function getEntityCapabilityForDeliveryActivity(
  activity: DemandDeliveryActivity
): EntityCapabilityId | undefined {
  return DEMAND_DELIVERY_ACTIVITY_TO_ENTITY_CAPABILITY[activity];
}

/** Resolve the entity directory capability id for a demand tactic, if one exists. */
export function getEntityCapabilityForTactic(tactic: DemandTactic): EntityCapabilityId | undefined {
  return DEMAND_TACTIC_TO_ENTITY_CAPABILITY[tactic];
}

/** Resolve the entity directory channel-expertise id for a demand channel, if one exists. */
export function getEntityChannelExpertiseForChannel(
  channel: DemandChannel
): EntityChannelExpertiseId | undefined {
  return DEMAND_CHANNEL_TO_ENTITY_CHANNEL_EXPERTISE[channel];
}

/**
 * Directory facet ids resolved from a content record's demand attributes.
 *
 * `capabilities` combines the capability equivalents of both the record's
 * tactics and its delivery activities; `channelExpertise` maps its demand
 * channels. All lists are de-duplicated. Use this to build a "Show Providers"
 * link from an inspiration/example record to a pre-filtered /directory view.
 */
export interface ResolvedDirectoryFacets {
  capabilities: EntityCapabilityId[];
  channelExpertise: EntityChannelExpertiseId[];
}

/**
 * Resolve a content record's demand attributes to directory filter facets.
 *
 * Accepts a partial set of attributes so it works with records that only carry
 * some facets. Returns deduped capability + channel-expertise id arrays ready
 * to feed into a multi-value /directory href.
 */
export function resolveDirectoryFacetsFromDemandAttributes(
  attributes: Partial<
    Pick<DemandInspirationAttributes, 'tactics' | 'deliveryActivities' | 'channels'>
  >
): ResolvedDirectoryFacets {
  const capabilities = new Set<EntityCapabilityId>();
  const channelExpertise = new Set<EntityChannelExpertiseId>();

  for (const tactic of attributes.tactics ?? []) {
    const capability = getEntityCapabilityForTactic(tactic);
    if (capability) capabilities.add(capability);
  }

  for (const activity of attributes.deliveryActivities ?? []) {
    const capability = getEntityCapabilityForDeliveryActivity(activity);
    if (capability) capabilities.add(capability);
  }

  for (const channel of attributes.channels ?? []) {
    const expertise = getEntityChannelExpertiseForChannel(channel);
    if (expertise) channelExpertise.add(expertise);
  }

  return {
    capabilities: [...capabilities],
    channelExpertise: [...channelExpertise],
  };
}

/** True when the resolved facets contain at least one directory filter value. */
export function hasResolvedDirectoryFacets(facets: ResolvedDirectoryFacets): boolean {
  return facets.capabilities.length > 0 || facets.channelExpertise.length > 0;
}
